import React, { useState } from 'react';
import PropTypes from "prop-types";
import { Trans, useTranslation } from 'react-i18next';

import { Stage } from '@pixi/react';
import { Toast } from "bootstrap/js/dist/toast";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

import "~/shared/prefilter";
import { snakeCase } from "~/shared/utils";
import { setRandomOrder } from './utils';
import GameApp from "../components/GameApp/GameApp";
import PlayerMenuPanel from "../components/PlayerMenuPanel/PlayerMenuPanel";
import { LearningTutorialPanel, TransferTutorialPanel } from '../components/TutorialPanel/TutorialPanel';

const STATUSES = {
    LANDING: 0,
    ADULT: 1,
    CONSENT: 2, 
    PLAYER_DATA: 3,
    LEARNING_TUTORIAL: 4,
    TRANSFER_TUTORIAL: 5,
    GAME: 6,
    END: 7,
};

const IndexApp = (props) => {
    const { t } = useTranslation(["common", "glossary", "Index/IndexApp"]);

    const [status, setStatus] = useState(STATUSES.LANDING);
    const [playerData, setPlayerData] = useState({
        isAdult: null,
        gender: null,
        childAge: null,
        adultAgeSpan: null,
        levelOfEducation: null,
        firstTime: null
    });

    const [session, setSession] = useState({
        id: null,
        patterns: null,
        step: null,
        phase: null,
        answers: null,
        nbLearningAnswers: 0,
    });

    const [timeoutFlag, setTimeoutFlag] = useState(true);

    const nbLearningPatterns = session.patterns ? session.patterns.filter(item => item[1] < 3).length : 0;
    const nbTransferPatterns = session.patterns ? session.patterns.filter(item => item[1] >= 3).length : 0;

    const postPlayerData = () => {
        $.post(
            props.gameSessionStartApiEndpoint,
            playerData.isAdult ? {
                gender: playerData.gender,
                adult_age_span: playerData.adultAgeSpan,
                level_of_education: playerData.levelOfEducation,
                first_time_playing: playerData.firstTime,
                difficulty: playerData.difficulty
            } : {
                gender: playerData.gender,
                child_age: playerData.childAge,
                first_time_playing: playerData.firstTime,
                difficulty: playerData.difficulty
            },
            (response) => {
                setSession({
                    id: response.session,
                    step: 0,
                    phase: 'learning1',
                    patterns: setRandomOrder(response.patterns),
                    answers: [],
                });
            }
        )
        .fail(() => {
            Toast.getOrCreateInstance($("error-toast")[0]).show();
            handleStop();
        });
    };
    const postSessionStop = () => {
        $.ajax({
            type: "POST",
            contentType: "application/json",
            url: props.gameSessionStopApiEndpoint,
            data: JSON.stringify({
                id: session.id,
                data: session.answers.map((item) => {
                    const updatedEntries = Object.entries(item).map(kvPair => [snakeCase(kvPair[0]), kvPair[0] == "phase" ? kvPair[1].replace(/\d/g, '') : kvPair[1]]);
                    return Object.fromEntries(updatedEntries);
                }),
            }),
            error: (response) => { console.log(response); }
        });
        setSession({
            id: null,
            patterns: null,
            step: null,
            phase: null,
            answers: null,
        });
    };
    const postSessionFinish = () => {
        $.ajax({
            type: "POST",
            contentType: "application/json",
            url: props.gameSessionFinishApiEndpoint,
            data: JSON.stringify({
                id: session.id,
                data: session.answers.map((item) => {
                    const updatedEntries = Object.entries(item).map(kvPair => [snakeCase(kvPair[0]), kvPair[0] == "phase" ? kvPair[1].replace(/\d/g, '') : kvPair[1]]);
                    return Object.fromEntries(updatedEntries);
                }),
            }),
            success: (response) => {
                setTimeout(() => { window.location.href = response.next; }, 5000);
                $("#redirect-button").attr("href", response.next);
            },
            error: (response) => { console.log(response); }
        });
        setSession({
            id: null,
            patterns: null,
            step: null,
            phase: null,
            answers: null,
        });
    };

    const handleStart = () => setStatus(STATUSES.ADULT);
    const handleAdultSelect = (event) => { setPlayerData({ ...playerData, ...{ isAdult: event.target.value == "Yes" ? true : false }}); setStatus(STATUSES.CONSENT); };
    const handleConsentSelect = () => setStatus(STATUSES.PLAYER_DATA);
    const handleTutorialStart = () => { postPlayerData(); setStatus(STATUSES.LEARNING_TUTORIAL); };
    const handleGameStart = () => setStatus(STATUSES.GAME);
    const handleStop = () => {
        setPlayerData({
            isAdult: null,
            gender: null,
            childAge: null,
            adultAgeSpan: null,
            levelOfEducation: null,
            firstTime: null
        });
        if(session.id) postSessionStop();
        setStatus(STATUSES.LANDING);
    };

    const handlePlayerDataUpdate = (data) => { setPlayerData({ ...playerData, ...data }); };
    const handlePlayerAnswer = (answer, answerTime) => {
        const [pattern, category] = session.patterns[session.step];
        console.log(pattern, answer == category);
        session.answers = [     // No need for rerendering when updating answers
            ...session.answers,
            ...[
                {
                    ...{
                        pattern: pattern,
                        phase: session.phase,
                        answer: answer,
                        timeToAnswer: answerTime,
                        category: category,
                    },
                    ...session.phase.includes("learning") ? {
                        isCorrect: answer == category,
                    } : {}
                }
            ]
        ];
        if(session.phase.includes("learning") && answer != "timeout") session.nbLearningAnswers += 1;
    };
    const handleStepUpdate = () => {
        const indexLastLearningPattern = session.patterns.filter((item) => item[1] < 3).length - 1;
        const indexLastTransferPattern = indexLastLearningPattern + session.patterns.filter((item) => item[1] == 3).length;

        if(session.phase == 'learning1') {
            const isCorrect = timeoutFlag ? false : session.answers[session.answers.length - 1].isCorrect;

            if(session.step < indexLastLearningPattern)
                setSession({
                    ...session,
                    ...{
                        step: isCorrect ? session.step + 1 : 0,
                        phase: 'learning1',
                        patterns: isCorrect ? session.patterns : setRandomOrder(session.patterns)
                    }
                });
            else {
                setSession({
                    ...session,
                    ...{
                        step: 0,
                        phase: isCorrect ? 'learning2' : 'learning1',
                        patterns: setRandomOrder(session.patterns)
                    }
                });
            }
        } else if(session.phase == 'learning2') {
            const isCorrect = timeoutFlag ? false : session.answers[session.answers.length - 1].isCorrect;

            if(session.step < Math.floor(indexLastLearningPattern / 2)) // Less answers in learning2
                setSession({
                    ...session,
                    ...{
                        step: isCorrect ? session.step + 1 : 0,
                        phase: isCorrect ? 'learning2' : 'learning1',
                        patterns: isCorrect ? session.patterns : setRandomOrder(session.patterns)
                    }
                });
            else {
                setSession({
                    ...session,
                    ...{
                        step: isCorrect ? indexLastLearningPattern + 1 : 0,
                        phase: isCorrect ? 'transfer1' : 'learning1',
                        patterns: isCorrect ? session.patterns : setRandomOrder(session.patterns)
                    }
                });
                if(isCorrect)
                    setStatus(STATUSES.TRANSFER_TUTORIAL);
            }
        } else if (session.phase == "transfer1") {
            if(session.step < indexLastTransferPattern)
                setSession({ ...session, ...{ step: session.step + 1, phase: 'transfer1' }});
            else
                setSession({
                    ...session,
                    ...{
                        step: indexLastLearningPattern + 1,
                        phase: "transfer2",
                        patterns: setRandomOrder(session.patterns)
                    }
                });
        } else if (session.phase == "transfer2") {
            if(session.step < indexLastTransferPattern)
                setSession({ ...session, ...{ step: session.step + 1, phase: 'transfer2' }});
            else
                setSession({
                    ...session,
                    ...{
                        step: indexLastLearningPattern + 1,
                        phase: "transfer3",
                        patterns: setRandomOrder(session.patterns)
                    }
                });
        } else {
            if(session.step < indexLastTransferPattern)
                setSession({ ...session, ...{ step: session.step + 1, phase: 'transfer3' }});
            else {
                setStatus(STATUSES.END);
                postSessionFinish();
            }
        }
    };

    const updateTimeoutFlag = (value) => { setTimeoutFlag(value); };

    return <>
        <div className = "text-center position-fixed top-0 bottom-0 start-0 end-0">
            <div className = "position-fixed top-0 bottom-0 start-0 end-0" id = "stars"></div>
            <div className = "position-absolute top-0 bottom-0 end-0 h-100" id = "twinkling"></div>
            <div id = "error-toast" className = "toast position-absolute top-0 end-0 mt-3 me-3 align-items-center text-bg-danger border-0" role = "alert" aria-live = "assertive" aria-atomic = "true">
                <div className = "d-flex">
                    <div className = "toast-body">{ t("error-msg") }</div>
                    <button type = "button" className = "btn-close me-2 m-auto" data-bs-dismiss = "toast" aria-label = "Close"></button>
                </div>
            </div>
            <div className = 'd-flex flex-column justify-content-center align-items-center h-100 w-100'>
                <div
                    id = "landing-menu"
                    className = 'container'
                    style = {
                        status == STATUSES.LANDING ?
                            { opacity: 1, zIndex: 3, height: "50vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    <h1 className = 'display-1'>MEL</h1>
                    <h3 className = 'display-4'>Modeling and Estimating Learning</h3>
                    {
                        !(props.images.length > 1) ?
                            <p className = 'text-danger'>{ t("Index/IndexApp:database-error-msg") }</p> :
                            <button className = 'btn btn-lg btn-light fs-2 mt-3' onClick = { handleStart }>
                                { t("glossary:start") }
                            </button>
                    }
                </div>
                <div
                    id = "adult-menu"
                    className = 'container'
                    style = {
                        status == STATUSES.ADULT ?  // FOR SMOOTH TRANSITION
                            { opacity: 1, zIndex: 3, height: "50vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    {
                        status == STATUSES.ADULT ?  // NEW INSTANCE EVERY TIME
                            <>
                                <div className = 'display-3'>
                                    { t("Index/IndexApp:adultMenu.title") }
                                </div>
                                <div className = "btn-group btn-group-lg mt-5" role = "group">
                                    <input
                                        type = "radio"
                                        className = "btn-check"
                                        name = "adult"
                                        id = "yes"
                                        value = "Yes"
                                        autoComplete = "off"
                                        onClick = { handleAdultSelect }
                                    />
                                    <label className = "btn btn-outline-light fs-2" htmlFor = "yes">{ t("glossary:yes") }</label>
                                    <input
                                        type = "radio"
                                        className = "btn-check"
                                        name = "adult"
                                        id = "no"
                                        value = "No"
                                        autoComplete = "off"
                                        onClick = { handleAdultSelect }
                                    />
                                    <label className = "btn btn-outline-light fs-2" htmlFor = "no">{ t("glossary:no") }</label>
                                </div>
                            </> :
                            null
                    }
                </div>
                <div
                    id = "consent-menu"
                    className = 'container overflow-auto'
                    style = {
                        status == STATUSES.CONSENT ?    // FOR SMOOTH TRANSITION
                            { opacity: 1, zIndex: 3, height: "75vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    {
                        status == STATUSES.CONSENT ?    // NEW INSTANCE EVERY TIME
                            <div>
                                <div className = 'display-3'>{ t("Index/IndexApp:consentMenu.title") }</div>
                                    {
                                        !playerData.isAdult ?
                                            <p className = 'fst-italic'>{ t("Index/IndexApp:consentMenu.child.subtitle") }</p> :
                                            null
                                    }
                                    <p className = 'fst-italic'>{ t("Index/IndexApp:consentMenu.readWarning") }</p>
                                    <p>{ playerData.isAdult ? t("Index/IndexApp:consentMenu.adult.headline") : t("Index/IndexApp:consentMenu.child.headline") }</p>
                                    <div className = 'd-flex justify-content-center align-content-center flex-wrap fs-4'>
                                        <a
                                            className = 'contributor m-3 text-center link-light'
                                            href = "https://math.univ-cotedazur.fr/~reynaudb/"
                                            target = "_blank"
                                            rel = "noreferrer"
                                        >
                                            <div className = 'fw-bold'>{ t("Index/IndexApp:consentMenu.headOfProject").toUpperCase() } - Patricia Reynaud-Bouret</div>
                                            <div className = 'fs-5'>
                                                <div>{ t("Index/IndexApp:consentMenu.headOfProjectTitles") }</div>
                                                <div><span className = 'bi bi-envelope-at-fill'></span> patricia.Reynaud-Bouret@univ-cotedazur.fr</div>
                                                <div><span className = 'bi bi-telephone-fill'></span> +33(0)4 89 15 04 96</div>
                                            </div>
                                        </a>
                                        <a
                                            className = 'contributor m-3 text-center link-light'
                                            href = "https://sites.google.com/view/sophiejaffard/accueil"
                                            target = "_blank"
                                            rel = "noreferrer"
                                        >
                                            <div className = 'fw-bold'>{ t("Index/IndexApp:consentMenu.mainInvestigator").toUpperCase() } - Sophie Jaffard</div>
                                            <div className = 'fs-5'>
                                                <div>{ t("Index/IndexApp:consentMenu.mainInvestigatorTitles") }</div>
                                                <div><span className = 'bi bi-envelope-at-fill'></span> sophie.Jaffard@univ-cotedazur.fr</div>
                                            </div>
                                        </a>
                                    </div>
                                    <div className = 'my-3'>
                                        <p>{ t(`Index/IndexApp:consentMenu.${playerData.isAdult ? "adult" : "child"}.firstParagraph`) }</p>
                                        <p>{ t(`Index/IndexApp:consentMenu.${playerData.isAdult ? "adult" : "child"}.secondParagraph`) }</p>
                                        <p>
                                            <Trans
                                                t = { t }
                                                i18nKey = { `Index/IndexApp:consentMenu.${playerData.isAdult ? "adult" : "child"}.thirdParagraph` }
                                                components = {{ closeButton: <span className = 'bi bi-x-lg' /> }}
                                            />
                                        </p>
                                    </div>
                                    <button className = 'btn btn btn-light fs-3 mt-3' onClick = { handleConsentSelect }>
                                        { t(`Index/IndexApp:consentMenu.${playerData.isAdult ? "adult" : "child"}.agreeButton`) }
                                    </button>       
                            </div> :
                            null
                    }
                </div>
                <div
                    id = "player-menu"
                    className = 'container'
                    style = {
                        status == STATUSES.PLAYER_DATA ?    // FOR SMOOTH TRANSITION
                            { opacity: 1, zIndex: 3, height: "60vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    {
                        status == STATUSES.PLAYER_DATA ?    // NEW INSTANCE EVERY TIME
                            <PlayerMenuPanel
                                data = {
                                    playerData.isAdult ? {
                                        gender: playerData.gender,
                                        adult_age_span: playerData.adultAgeSpan,
                                        level_of_education: playerData.levelOfEducation,
                                        first_time_playing: playerData.firstTime,
                                        difficulty: playerData.difficulty
                                    } : {
                                        gender: playerData.gender,
                                        child_age: playerData.childAge,
                                        first_time_playing: playerData.firstTime,
                                        difficulty: playerData.difficulty
                                    }
                                }
                                isAdult = { playerData.isAdult }
                                updatePlayerData = { handlePlayerDataUpdate }
                                handleTutorialStart = { handleTutorialStart }
                            /> :
                            null
                    }
                </div>
                <div
                    id = "tutorial-menu"
                    className = "container p-3 fs-4"
                    style = {
                        (status == STATUSES.LEARNING_TUTORIAL) || (status == STATUSES.TRANSFER_TUTORIAL) ?
                            { opacity: 1, zIndex: 3, maxHeight: "75vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    {
                        status == STATUSES.LEARNING_TUTORIAL ?
                            <LearningTutorialPanel
                                nbLearningPatterns = { nbLearningPatterns + Math.floor(nbLearningPatterns / 2) }
                                startGame = { handleGameStart }
                            /> :
                            null
                    }
                    {
                        status == STATUSES.TRANSFER_TUTORIAL ?
                            <TransferTutorialPanel
                                nbTransferPatterns = { nbTransferPatterns }
                                resumeGame = { handleGameStart }
                            /> :
                            null
                    }
                </div>
                <div
                    id = "end-panel"
                    className = "container p-3"
                    style = {
                        status == STATUSES.END ?
                            { opacity: 1, zIndex: 3, height: "50vh", overflow: "auto" } :
                            { opacity: 0, zIndex: -1, height: 0, overflow: "hidden" }
                    }
                >
                    <p className = 'display-3'>{ t("glossary:congratulations") } !</p>
                    <p className = 'fs-1'>{ t("Index/IndexApp:endPanel.headline") }</p>
                    <p>
                        <Trans
                            t = { t }
                            i18nKey = { "Index/IndexApp:endPanel.text" }
                            components = {[<a key = { 0 } className = "link-warning" id = "redirect-button" href = "#" />]}
                        />
                    </p>
                </div>
            </div>
            <div
                id = "game-container"
                className = 'position-absolute top-0 bottom-0 end-0 h-100'
                style = { status == STATUSES.GAME ? { zIndex: 4 } : { zIndex: -1 } }
            >
                <div
                    id = "in-game-feedback"
                    className = 'position-absolute w-100 text-center mt-5 fs-3'
                    style = { status == STATUSES.GAME ? { opacity: 1 } : { opacity: 0 }}
                >
                    <p>{ t("Index/IndexApp:gameContainer.headline") }</p>
                    <p className = 'fst-italic'>{ session.phase?.includes("learning") ? t("Index/IndexApp:gameContainer.learningLabel") : null }</p>
                    <p className = 'fst-italic'>{ session.phase?.includes("transfer") ? t("Index/IndexApp:gameContainer.transferLabel") : null }</p>
                    {
                        session.phase ?
                            <>
                                {
                                    session.phase == "learning1" ?
                                        <>
                                            {
                                                [...new Array(nbLearningPatterns)].map((val, i) => {
                                                    let answer = session.answers.findLast(item => item.pattern == session.patterns[i][0]);
                                                    if (session.step <= i) answer = null;
                                                    
                                                    if(session.step == i)
                                                        return <span key = { i } id = "countdown-timer-clock" className = 'd-inline-block align-text-top'>
                                                            <CountdownCircleTimer
                                                                key = { Date.now() }
                                                                isPlaying = { !timeoutFlag & status != STATUSES.LEARNING_TUTORIAL }
                                                                size = { 28 }
                                                                duration = { 5 }
                                                                colors = "#f8f9fa"
                                                                trailColor = "rgba(0, 0, 0, 0)"
                                                                strokeLinecap = "butt"
                                                                rotation = "counterclockwise"
                                                                strokeWidth = { 14 }
                                                                trailStrokeWidth = { 14 }
                                                                onComplete = { () => { updateTimeoutFlag(true); return { shouldRepeat: true }; } }
                                                            />
                                                        </span>;

                                                    return <span
                                                        key = { i }
                                                        className = { answer && answer.isCorrect ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-danger' }
                                                        style = { !answer ? { opacity: 0 } : { opacity : 1 } }
                                                    />;
                                                })
                                            }
                                        </> :
                                        null
                                }
                                {
                                    session.phase == "learning2" ?
                                        <>
                                            <div>
                                                {
                                                    [...new Array(nbLearningPatterns)].map((_, i) => {
                                                        return <span key = { i } className = 'bi bi-check-circle-fill text-success'/>;
                                                    })
                                                }
                                            </div>
                                            {
                                                [...new Array(Math.floor(nbLearningPatterns / 2))].map((val, i) => {
                                                    let answer = session.answers.findLast(item => item.pattern == session.patterns[i][0]);
                                                    if (session.step <= i) answer = null;
                                                    
                                                    if(session.step == i)
                                                        return <span key = { i } id = "countdown-timer-clock" className = 'd-inline-block align-text-top'>
                                                            <CountdownCircleTimer
                                                                key = { Date.now() }
                                                                isPlaying = { !timeoutFlag & status != STATUSES.LEARNING_TUTORIAL }
                                                                size = { 28 }
                                                                duration = { 5 }
                                                                colors = "#f8f9fa"
                                                                trailColor = "rgba(0, 0, 0, 0)"
                                                                strokeLinecap = "butt"
                                                                rotation = "counterclockwise"
                                                                strokeWidth = { 14 }
                                                                trailStrokeWidth = { 14 }
                                                                onComplete = { () => { updateTimeoutFlag(true); return { shouldRepeat: true }; } }
                                                            />
                                                        </span>;

                                                    return <span
                                                        key = { i }
                                                        className = { answer && answer.isCorrect ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-danger' }
                                                        style = { !answer ? { opacity: 0 } : { opacity : 1 } }
                                                    />;
                                                })
                                            }
                                        </> :
                                        null
                                }
                                {
                                    session.phase.includes("transfer") ?
                                        <>
                                            <span id = "countdown-timer-clock" className = 'd-inline-block align-text-top'>
                                                <CountdownCircleTimer
                                                    key = { Date.now() }
                                                    isPlaying = { !timeoutFlag & status != STATUSES.TRANSFER_TUTORIAL }
                                                    size = { 28 }
                                                    duration = { 5 }
                                                    colors = "#f8f9fa"
                                                    trailColor = "rgba(0, 0, 0, 0)"
                                                    strokeLinecap = "butt"
                                                    rotation = "counterclockwise"
                                                    strokeWidth = { 14 }
                                                    trailStrokeWidth = { 14 }
                                                    onComplete = { () => { updateTimeoutFlag(true); return { shouldRepeat: true }; } }
                                                />
                                            </span>
                                        </> :
                                        null
                                }
                            </> :
                            null
                    }
                </div>
                <Stage
                    width = { window.innerWidth }
                    height = { window.innerHeight }
                    options = {{
                        resizeTo: window,
                        backgroundAlpha: 0,
                        antialias: true,
                    }}
                >
                    <GameApp
                        images = { props.images }
                        session = { session }
                        updatePlayerAnswer = { handlePlayerAnswer }
                        updateStep = { handleStepUpdate }
                        playing = { status == STATUSES.GAME }
                        timeoutFlag = { timeoutFlag }
                        updateTimeoutFlag = { updateTimeoutFlag }
                    />
                </Stage>
            </div>
            <button
                type = "button"
                className = "btn link-light rolling-btn rolling-btn-end btn position-absolute top-0 start-0"
                id = "close-button"
                data-bs-toggle = "modal"
                data-bs-target = "#quit-game-modal"
                style = {
                    status != STATUSES.LANDING && status != STATUSES.END ?
                        { opacity: 1, zIndex: 4 } :
                        { opacity: 0, zIndex: -1 }
                }
            >
                <i className = 'rolling-btn-icon bi bi-x-lg'></i>
                <span className = 'rolling-btn-text'>{ t("Index/IndexApp:quitGameButton") }</span>
            </button>
        </div>
        <div className = "modal fade" id = "quit-game-modal" tabIndex = "-1" aria-labelledby = "quit-game-modal-label" aria-hidden = "true">
            <div className = "modal-dialog modal-dialog-centered">
                <div className = "modal-content">
                    <div className = "modal-header text-light bg-primary">
                        <h1 className = "modal-title fs-5" id = "quit-game-modal-label">{ t("Index/IndexApp:quitGameButton") }</h1>
                        <button type = "button" className = "btn-close btn-close-white" data-bs-dismiss = "modal" aria-label = "Close"></button>
                    </div>
                    <div className = "modal-body text-dark">
                        { t("Index/IndexApp:quitGameText")}
                    </div>
                    <div className = "modal-footer">
                        <button type = "button" className = "btn btn-secondary" data-bs-dismiss = "modal">{ t("glossary:cancel") }</button>
                        <button type = "button" className = "btn btn-primary bg-primary" data-bs-dismiss = "modal" onClick = { handleStop }>{ t("glossary:quit") }</button>
                    </div>
                </div>
            </div>
        </div>
    </>;
};

IndexApp.propTypes = {
    images: PropTypes.arrayOf(PropTypes.object),
    gameSessionStartApiEndpoint: PropTypes.string,
    gameSessionFinishApiEndpoint: PropTypes.string,
    gameSessionStopApiEndpoint: PropTypes.string,
};

export default IndexApp;
